import Debug from "../../../Debug";
import { removeSpaces } from "../../../utils";
import type { TradeInNotificationType } from "./tradeInAemUtilTypes";
import { FinanceUpdateTrigger, TradeInNotificationTypeEnum } from "./tradeInAemUtilTypes";

/**
 * For a list of possible scenario's see https://cde.toyota-europe.com/confluence/display/OR/Finance+scenarios
 */
export const getTradeInNotificationConfig = (
    msgCode: string,
    trigger: FinanceUpdateTrigger,
    depositDiff: number = 0,
    logInfo: boolean,
    previousNotificationType: TradeInNotificationTypeEnum | null,
    previousShow: boolean,
    allowedTradeinProduct: string,
    productName?: string,
): TradeInNotificationType => {
    const config: TradeInNotificationType = { notificationType: null, isError: false, show: false };

    // 104 and 105 are deprecated, but keeping it for backwards compatibility.
    config.isError = ["102", "104", "105"].includes(msgCode);

    switch (msgCode) {
        case "100":
            if ([FinanceUpdateTrigger.PxAdded, FinanceUpdateTrigger.NewQuote].includes(trigger)) {
                // Scenario 1
                config.notificationType = TradeInNotificationTypeEnum.Added;
                if (logInfo) Debug.info("Trade-in finance scenario 1");
            } else if (trigger === FinanceUpdateTrigger.NewConfig) {
                // Scenario 6
                config.notificationType = TradeInNotificationTypeEnum.AddedCarValue;
                if (logInfo) Debug.info("Trade-in finance scenario 6");
            }
            break;
        case "101":
            if ([FinanceUpdateTrigger.PxAdded, FinanceUpdateTrigger.NewQuote].includes(trigger)) {
                // Scenario 2
                config.notificationType = TradeInNotificationTypeEnum.ReducedDeposit;
                if (logInfo) Debug.info("Trade-in finance scenario 2");
            } else if (trigger === FinanceUpdateTrigger.NewConfig) {
                // Scenario 5A
                config.notificationType = TradeInNotificationTypeEnum.ReducedDepositCarValue;
                if (logInfo) Debug.info("Trade-in finance scenario 5a");
            }
            break;
        case "102":
        case "104":
        case "105":
            if ([FinanceUpdateTrigger.PxAdded, FinanceUpdateTrigger.NewQuote].includes(trigger)) {
                // Scenario 3
                config.notificationType = TradeInNotificationTypeEnum.Refused;
                if (logInfo) Debug.info("Trade-in finance scenario 3");
            } else if (trigger === FinanceUpdateTrigger.NewConfig) {
                if (depositDiff === 0) {
                    // Scenario 5B
                    config.notificationType = TradeInNotificationTypeEnum.RefusedCarValue;
                    // OR-1356 - Do not show popup when retrieving an error message because price is higher than maximum value.
                    config.isError = true;
                    config.show = false;
                    if (logInfo) Debug.info("Trade-in finance scenario 5b");
                } else if (depositDiff > 0) {
                    // Scenario 5C
                    config.notificationType = TradeInNotificationTypeEnum.RefusedCarValueDeposit;
                    if (logInfo) Debug.info("Trade-in finance scenario 5c");
                }
            }
            break;
    }

    if (
        productName &&
        allowedTradeinProduct &&
        !removeSpaces(allowedTradeinProduct).split(",").includes(removeSpaces(productName))
    ) {
        config.notificationType = TradeInNotificationTypeEnum.NotSupported;
        config.isError = true;
        if (logInfo) Debug.info("Trade-in scenario Not supported");
    }

    if (!config.notificationType && logInfo) {
        Debug.warn(`No PX message. Trigger ${trigger}, MsgCode ${msgCode}, DepositDiff ${depositDiff}`);
    }

    // Show the notification if there is a new label or the previous notification is still shown.
    config.show = config.notificationType !== previousNotificationType || previousShow;

    return config;
};
export const getTradeInCashNotificationConfig = (
    vehicleValue: number,
    currentPrice: number,
    previousNotificationType: TradeInNotificationTypeEnum | null,
    previousShow: boolean,
): TradeInNotificationType => {
    const config: TradeInNotificationType = { notificationType: null, isError: false, show: false };

    if (vehicleValue <= currentPrice) {
        config.notificationType = TradeInNotificationTypeEnum.AddedCash;
    } else {
        config.notificationType = TradeInNotificationTypeEnum.RefusedCash;
        config.isError = true;
    }

    // Show the notification if there is a new label or the previous notification is still shown.
    config.show = config.notificationType !== previousNotificationType || previousShow;

    return config;
};
